import {log, Random} from "@dp-instant/utils";
import {Action, di, GlobalEventProvider, lazyGet, lazyInject} from "@dp-instant/core";
import {Types2D, TypesFlow, TypesSocial, TypesUI} from "@dp-instant/interfaces";
import {ILeaderboard, ISocial, IUser, ScoreType} from "@dp-instant/social";
import {UIEvents} from "@dp-instant/ui";
import {SIOConstants} from "@game/constants";
import {ActiveBuildingsQuery} from "@game/coregame/ecs/queries/ActiveBuildingsQuery";
import {AllBuildingsQuery} from "@game/coregame/ecs/queries/AllBuildingsQuery";
import {BotsSystem} from "@game/coregame/ecs/systems/BotsSystem";
import {FighterMovementSystem} from "@game/coregame/ecs/systems/FighterMovementSystem";
import {GamePlaySystem} from "@game/coregame/ecs/systems/GamePlaySystem";
import {InitStageSystem} from "@game/coregame/ecs/systems/InitStageSystem";
import {InputSystem} from "@game/coregame/ecs/systems/InputSystem";
import {LevelEndSystem} from "@game/coregame/ecs/systems/LevelEndSystem";
import {PathsGenerationSystem} from "@game/coregame/ecs/systems/PathsGenerationSystem";
import {PopulationSystem} from "@game/coregame/ecs/systems/PopulationSystem";
import {GameState} from "@game/coregame/model/GameState";
import {MetaModel} from "@game/coregame/model/MetaModel";
import {TypesGame} from "@game/TypesGame";
import {ParticipantItem} from "@game/ui/interface/ParticipantItem";
import {PopupType} from "@game/ui/PopupType";
import {injectable} from "inversify";
import {Engine} from "tick-knock";
import {TutorialSystem} from "../ecs/systems/TutorialSystem";
import {GameEvents} from "../GameEvents";
import {PlayerType} from "../interfaces/PlayerType";
import {FieldView} from "../view/FieldView";
import {ContinentModel} from "./ContinentModel";
import {CookieModel} from "./CookieModel";
import {ScreenShotActionSIOPayload} from "@game/coregame/actions/ScreenShotActionSIO";
import {Ticker} from "pixi.js-legacy";
import {SkinManager} from "@game/coregame/managers/SkinManager";
import {UpdateSkinsSystem} from "@game/coregame/ecs/systems/UpdateSkinsSystem";
import {DestroyFieldAction} from "@game/coregame/actions/DestroyFieldAction";
import {SpritesPool} from "@game/coregame/utils/SpritesPool";
import {CTXLevelProgress} from "@game/coregame/interfaces/CTXLevelProgress";
import {hapticFirework} from "@game/haptic";

type OfflineEarning = {
    reward: number;
    isRewarded: boolean;
    date: Date;
};
const endStage = (win = false) => {
    lazyGet<Action<boolean>>(TypesGame.actions.endStage)?.run(win);
};
const endLevel = (win = false) => {
    lazyGet<Action<boolean>>(TypesFlow.LevelEnd)?.run(win);
};
const with_ext = (name: string, ext = "svg") =>
    name.endsWith(`.${ext}`) ? name : `${name}.${ext}`;

@injectable()
export class GameModel extends GlobalEventProvider {
    public static readonly LEVELS_PREDEFINED = [
        "United-States.svg",
        "Latin-America.svg",
        "Africa.svg",
        "UK.svg",
        "EuropeanUnion.svg",
        "Italy.svg",
        "Arabic.svg",
        "Central-Asia.svg",
        "Japan.svg",
        "China,Russia,Korea,Japan.svg",
        "Korea.svg",
        "China-Oceania-Australia.svg",
        "United-States2.svg"
        // ...__LEVELS__
    ].filter((value, index, self) => self.indexOf(value) === index);

    public static readonly DEFAULT_CTX_DATA: CTXLevelProgress = {
        l: GameModel.LEVELS_PREDEFINED[0],
        c: 0,
        s: []
    };

    @lazyInject(MetaModel)
    public readonly meta: MetaModel;
    @lazyInject(TypesGame.cookieModel)
    public readonly cookie: CookieModel;
    @lazyInject(TypesSocial.model)
    public readonly social: ISocial;
    public readonly levels: {[key: string]: ContinentModel} = {};
    public readonly engine: Engine = new Engine();
    private _currentContinent: ContinentModel | null;
    private _paused: boolean;
    private _engineTick = (dt = 0) => this.engine.update(dt);
    private _state: GameState;
    public readonly screenshots: string[] = [];

    @lazyInject(TypesGame.skinManager)
    private skinManager: SkinManager;

    private gameplaySystems = [
        new PopulationSystem(),
        new BotsSystem(),
        new GamePlaySystem(),
        new InputSystem(),
        new FighterMovementSystem()
    ];

    constructor() {
        super();
        this.paused = false; // TODO: remove

        if (process.env.NODE_ENV === "development") {
            Object.defineProperty(this, "d", {
                get: () => {
                    const social = this.social;
                    log.info(
                        `Level: ${this.currentContinent.data.id} stage: ${this.currentContinent.stageLevel} SOLO: ${social.inSolo}`
                    );
                    if (social.inSolo) {
                        return;
                    }
                    const prnt = (u: IUser, logFn = log.debug) =>
                        logFn(
                            `${u.name}${u === social.me ? " (You)" : ""} #${
                                u.scores.getScore(ScoreType.CONTEXT) || "-"
                            } --- ${u.scores.getEntry(ScoreType.CONTEXT)?.getExtraData()}}`
                        );
                    const ctxPlayers = social.contextPlayers;

                    console.group("players (#index --- score_history)");
                    ctxPlayers.forEach(u => prnt(u));
                    console.groupEnd();
                }
            });
        }
    }

    public get currentContinent(): ContinentModel {
        return this._currentContinent!;
    }

    private _rewardedAdsAllowed: boolean = SIOConstants.REWARDED_AD_ALLOWED;

    get rewardedAdsAllowed(): boolean {
        return this._rewardedAdsAllowed;
    }

    public offerX3(): boolean {
        return this._rewardedAdsAllowed && true;
    }

    public setCurrentContinent(model: ContinentModel, stage = 0) {
        if (this._currentContinent !== model) {
            this._currentContinent = model;
            this.engine.addQuery(ActiveBuildingsQuery);
            this.engine.addQuery(AllBuildingsQuery);
            this.engine.addSystem(new InitStageSystem(model, model.stageLevel));
        }
    }

    public startStage(model: ContinentModel = this._currentContinent!) {
        this.screenshots.splice(0, Number.MAX_SAFE_INTEGER); // clear all
        model.time.start();
        this.engine.addSystem(new InitStageSystem(model, model.stageLevel));
        this.engine.addSystem(new PathsGenerationSystem());
        this.state = GameState.GAMEPLAY;
    }

    public gotoNextLevelStage(): boolean {
        switch (this._state) {
            case GameState.WIN_CONTINENT:
                this.engine.clear();
                return false;
        }
        return true;
    }

    public endStage(win = false, forceToEndContinent = false) {
        if (win) {
            if (forceToEndContinent) {
                this.currentContinent.stageLevel = this.currentContinent.data.stages.length;
                this.cookie.currentStage = 0;
                this.state = GameState.WIN_CONTINENT;
                return;
            }

            this.currentContinent.captureStage();
            this.incrementContextScore();
            this.cookie.currentStage = this.currentContinent.stageLevel;
            if (this.currentContinent.isFinished) {
                this.cookie.currentStage = 0;
                this.state = GameState.WIN_CONTINENT;
            } else {
                this.state = GameState.WIN_STAGE;
            }
        } else {
            this.state = GameState.LOOSE;
        }
    }

    public restartLevel() {
        this.currentContinent.buildings.clear();
        this.engine.removeAllEntities();
        this.dispatch(GameEvents.RESTART_LEVEL);
    }

    get paused(): boolean {
        return this._paused;
    }

    set paused(value: boolean) {
        if (this._paused !== value) {
            this._paused = value;
            if (this._paused) {
                this._currentContinent?.time.pause();
                Ticker.shared.remove(this._engineTick);
            } else {
                this._currentContinent?.time.resume();
                Ticker.shared.add(this._engineTick);
            }
        }
    }
    get state(): GameState {
        return this._state;
    }

    set state(value: GameState) {
        if (this._state !== value) {
            this._state = value;
            this.onStateChanged();
        }
    }
    public async createFieldScreenShot(): Promise<string | undefined> {
        const view = di.get<FieldView>(TypesGame.views.fieldInstance);
        const screenshotAction = lazyGet<Action<ScreenShotActionSIOPayload>>(
            Types2D.screenShotAction
        );
        const screenShot = await screenshotAction?.run({image: view.map});
        if (screenShot) {
            this.screenshots.push(screenShot);
            while (this.screenshots.length > 2) {
                this.screenshots.shift();
            }
            return screenShot;
        } else {
            log.warn("Screenshot creation failed");
        }
    }

    public exitTheGame() {
        this.paused = true;
        this.dispatch(UIEvents.POPUP, {
            id: PopupType.CONFIRM,
            props: {
                onConfirm: async (success = false) => {
                    this.cookie.syncTime();
                    if (success) {
                        if (!this.social.inSolo) {
                            await this.social.playSolo();
                            await di.get<Action>(TypesFlow.LevelStart).run(); // force to reload map to show SOLO
                        }
                        this.state = GameState.LOBBY;
                    }
                    this.paused = false;
                    this.dispatch(UIEvents.POPUP, {id: null});
                }
            }
        });
    }
    public cancelTutorial() {
        const tutorialSystem = this.engine.getSystem(TutorialSystem);
        tutorialSystem && this.engine.removeSystem(tutorialSystem); // remove finger of first interaction
    }
    private onStateChanged() {
        const end = () => {
            this.dispatch(GameEvents.AIM_REMOVE); // Remove all arrows when state changed
            this.gameplaySystems.forEach(system => this.engine.removeSystem(system));
            this.engine.addSystem(new LevelEndSystem());
            this.cancelTutorial();
            // this.skinManager.selectSkins();
        };
        switch (this._state) {
            case GameState.LOOSE:
                end();
                endStage(false);
                break;
            case GameState.WIN_STAGE:
                end();
                setTimeout(hapticFirework, 500);
                this.createFieldScreenShot(); // save a photo of field view state
                endStage(true);
                break;
            case GameState.WIN_CONTINENT:
                end();
                setTimeout(hapticFirework, 500);
                this.createFieldScreenShot(); // save a photo of field view state
                endLevel(true);
                break;
            case GameState.LOBBY:
                end();
                this.dispatch(UIEvents.SCREEN_CHANGED, {id: TypesUI.screen.HOME});
                break;
            case GameState.GAMEPLAY:
                this.gameplaySystems.forEach(sys => this.engine.addSystem(sys));
                this.engine.addSystem(new TutorialSystem());
                this.dispatch(UIEvents.SCREEN_CHANGED, {
                    id: TypesUI.screen.GAMEPLAY,
                    props: {participants: this.getAssociatedUsers()}
                });
                break;
        }
        log.debug("GameState ->", GameState[this._state]);
        this.dispatch(GameEvents.STATE_CHANGED, this._state);
    }
    public get absoluteLevelNum(): number {
        return this.cookie.absoluteLevelNum;
    }
    public getAssociatedUsers(stage = this.currentContinent.stageLevel): ParticipantItem[] {
        const social = this.social;
        const participants: Map<PlayerType, IUser> = new Map();
        const contextUsers = social.inSolo ? [] : social.contextPlayers.slice(0);

        contextUsers.sort((a, b) => (a === social.me ? -1 : 0));

        const playersSet: Set<PlayerType> = new Set();
        this.currentContinent.parsed.forEach(data => {
            if (data.stage === stage) {
                playersSet.add(data.startOwner);
            }
        });

        const playersSetArray = Array.from(playersSet).sort((a, b) => a - b);

        for (let i = 0; i < playersSetArray.length; i++) {
            let associatedUser = null;
            switch (playersSetArray[i]) {
                case PlayerType.First:
                    associatedUser = contextUsers.shift();
                    break;
                case PlayerType.Neutral:
                    break;
                default:
                    associatedUser = contextUsers.pop();
            }
            if (associatedUser) {
                participants.set(playersSetArray[i], associatedUser);
            }
        }
        return Array.from(
            participants,
            ([owner, data]): ParticipantItem => ({
                owner,
                data,
                color: this.skinManager.getColorBy(owner)[0]
            })
        );
    }

    public goToLobby(): void {
        this.state = GameState.LOBBY;
        this.onStateChanged();
    }

    public getOfflineEarning(): OfflineEarning {
        let date = new Date(this.cookie.timeDiff);
        let rate = this.meta.getOfflineEarning();
        let isRewarded = false;
        let reward = 0;
        let seconds = this.cookie.timeDiff / 1000;
        if (seconds > SIOConstants.MIN_OFFLINE_EARNINGS_PERIOD_IN_MIN * 60) {
            let rateSeconds = rate / 60 / 60;
            let earning = rateSeconds * seconds;
            if (earning >= 1) {
                isRewarded = true;
                reward = Math.round(earning);
            }
        }
        return {isRewarded, reward, date};
    }
    public getLevelNameByAbsoluteNum(index: number): string {
        return GameModel.LEVELS_PREDEFINED[Math.floor(index % GameModel.LEVELS_PREDEFINED.length)];
    }

    public onShopScreenChanged() {
        this.skinManager.updateSkins();
        this.engine.addSystem(new UpdateSkinsSystem());
    }

    public async disposeCurrentLevel() {
        await di.get(DestroyFieldAction).run();
        this._currentContinent?.dispose();
        this._currentContinent = null;
        this.engine.clear();
        const sprites = di.get<SpritesPool>(TypesGame.spritesPool);
        sprites.purge();
    }
    public async incrementContextScore() {
        const ctxId = this.social.context_id;
        if (ctxId && !this.social.inSolo) {
            const contextLeaderboard = lazyGet<ILeaderboard>(TypesSocial.leaderboardContext);
            contextLeaderboard?.submit(
                this.social.me.scoreContext + 1,
                this.currentContinent.getHistory()
            );
        }
    }
    public getContextData(): CTXLevelProgress {
        if (this.social.inSolo) {
            return {
                l: this.getLevelNameByAbsoluteNum(this.absoluteLevelNum - 1),
                c: this.cookie.currentStage,
                s: []
            };
        }
        const ctx = this.social.context_id;
        if (ctx) {
            const contextEntry = this.social.me.scores.getEntry(ScoreType.CONTEXT);
            const contextDataString = contextEntry?.getExtraData();
            if (contextDataString) {
                const contextData = JSON.parse(contextDataString);
                if (contextData) {
                    const stage = contextData.c || 0;
                    const total = contextData.s.length || Number.MAX_SAFE_INTEGER;
                    if (stage >= total) {
                        const level = contextData.l || Random.from(GameModel.LEVELS_PREDEFINED);
                        let levelIndex = GameModel.LEVELS_PREDEFINED.indexOf(
                            with_ext(level, "svg")
                        );
                        if (levelIndex == -1) {
                            levelIndex = 0;
                        }
                        levelIndex++;
                        if (levelIndex >= GameModel.LEVELS_PREDEFINED.length) {
                            levelIndex = 0;
                        }
                        contextData.l = GameModel.LEVELS_PREDEFINED[levelIndex];
                        contextData.s = [];
                        contextData.c = 0;
                        log.debug("new context data generated", contextData);
                    }
                    return contextData;
                }
            }
        }
        return GameModel.DEFAULT_CTX_DATA;
    }
}
